// BUILD-046 Owner-requested layout revision (visual edits ledger, Layout-3): the text of the "Task due date" COLUMN of the aligned task table.
//
// Presentation only. It decides how a date is WRITTEN ("Today", "Tomorrow", "Oct 22", the year when it is not the current year, "Overdue ..."); it decides
// nothing about WHICH tasks are overdue: `overdue` is passed in by the caller from the existing logic (`rowIsOverdueOn` of taskOrdering.ts for the shared and team
// lists - the server's flag, plus, since the Owner-approved reconcile pass of 2026-10-20, a past Task due date on an UNCLAIMED task - and `taskIsOverdueOn` for My
// tasks) and is never recomputed here. A date-only string is never parsed with `new Date(string)` (that is UTC
// midnight, the previous day in Phoenix): the parts are split, and "tomorrow" is calendar arithmetic on those parts. "Today" is the Phoenix day the caller
// supplies (`phoenixToday()` of dateLabels.ts).
//
// Pure module. Erasable TypeScript only.

import { TASK_DUE_DATE_LABEL, isIsoDate } from './dateLabels';

const MONTHS: readonly string[] = Object.freeze(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']);

/** `2026-10-31` plus 1 is `2026-11-01`. Calendar arithmetic on the parts (UTC, so no zone can shift the day). Anything that is not a real date gives ''. */
export function addDays(iso: unknown, days: number): string {
  if (!isIsoDate(iso)) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const moved = new Date(Date.UTC(y, m - 1, d + days));
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${moved.getUTCFullYear()}-${pad(moved.getUTCMonth() + 1)}-${pad(moved.getUTCDate())}`;
}

/** `2026-10-22` is `Oct 22`; with a different year than `today`'s it is `Oct 22, 2027`. Not a date: ''. */
export function shortDate(iso: unknown, today: string): string {
  if (!isIsoDate(iso)) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const base = `${MONTHS[m - 1]} ${d}`;
  const currentYear = isIsoDate(today) ? Number(today.slice(0, 4)) : y;
  return y === currentYear ? base : `${base}, ${y}`;
}

export interface DueCell {
  /** What the column shows. '—' when the task has no (usable) date. */
  text: string;
  /** True when the date is shown in the overdue tone (the word "Overdue" is in `text`). */
  overdue: boolean;
  /** True when there is no date. */
  empty: boolean;
  /** Tooltip / accessible detail: the label and the full date (month/day/year, as elsewhere), or "No Task due date". */
  title: string;
}

/** The plain label of a date: Today, Tomorrow, Yesterday, or `Oct 22`. */
export function dueWord(iso: unknown, today: string): string {
  if (!isIsoDate(iso)) return '';
  if (iso === today) return 'Today';
  if (iso === addDays(today, 1)) return 'Tomorrow';
  if (iso === addDays(today, -1)) return 'Yesterday';
  return shortDate(iso, today);
}

/**
 * True when the Due column of a row WRITES the word "Overdue": the row is overdue by THE one rule (`overdue`, passed in) AND its Task due date is a real date, because
 * `describeDueCell` writes "Overdue" only beside a date. A row that is overdue by the server's flag alone, or whose date is not a date, shows a dash or the raw value there; its
 * "Overdue" is then NOT a duplicate of anything and must stay wherever else the row says it (BUILD-046 Team table cleanup: the row's attention text drops its own "Overdue" only when this is true).
 */
export function dueCellStatesOverdue(input: { dueDate: unknown; overdue: boolean }, today: string): boolean {
  return input.overdue === true && dueWord(input.dueDate, today) !== '';
}

export function describeDueCell(input: { dueDate: unknown; overdue: boolean }, today: string): DueCell {
  const word = dueWord(input.dueDate, today);
  if (word === '') {
    // A value that is not a date-only string (older data) is shown as given, not hidden.
    const raw = typeof input.dueDate === 'string' ? input.dueDate.trim() : '';
    if (raw !== '') return { text: raw, overdue: input.overdue, empty: false, title: `${TASK_DUE_DATE_LABEL} ${raw}` };
    return { text: '\u2014', overdue: false, empty: true, title: `No ${TASK_DUE_DATE_LABEL}` };
  }
  const [y, m, d] = (input.dueDate as string).split('-');
  const full = `${m}/${d}/${y}`;
  // Overdue reads "Overdue" first, then the date, so the word and not only the tone says it.
  const text = input.overdue ? `Overdue \u00b7 ${word}` : word;
  return { text, overdue: input.overdue, empty: false, title: `${TASK_DUE_DATE_LABEL} ${full}` };
}
